import React, {ChangeEvent, FunctionComponent, KeyboardEvent, MouseEvent, StrictMode, useEffect, useState,} from 'react'
import {LoadingIcon, LocationIcon} from "@App/Components/common/Icons"
import {Place} from "@App/types"
import {useDelayedInput} from "@App/hooks/useDelayedInput"
import {useDispatch, useSelector} from "react-redux"
import {actionCreators, State} from '@App/state'

import {bindActionCreators} from "@reduxjs/toolkit"
import {useGeoSuggest} from "@App/hooks/api/useGeoSuggest";
import {useTranslation} from "react-i18next";
import {useNavigateWithQueryParams} from "@App/hooks/useNavigateWithQueryParams";

type LocationSearchProps = {
    className?: string,
    onSubmit?: (place: Place) => void,
    shouldWriteToUrl?: boolean
}

export const LocationFilter: FunctionComponent<LocationSearchProps> = ({
                                                                           className,
                                                                           onSubmit,
                                                                           shouldWriteToUrl
                                                                       }: LocationSearchProps = {className: ''}) => {

    const {t} = useTranslation()
    const place = useSelector((state: State) => state.orders.place)

    const [lat, setLat] = useState(null)
    const [lon, setLon] = useState(null)
    const [openLocationSuggest, setOpenLocationSuggest] = useState(false)
    const [search, setSearch] = useState(place ? place.name : '')
    const [delayedSearch, setDelayedSearch] = useState(place ? place.name : '')

    const dispatch = useDispatch();
    const {choosePlace} = bindActionCreators(actionCreators, dispatch)

    const {loading, error, suggests} = useGeoSuggest({
        search: place && search === place.name ? search : delayedSearch,
        lat: lat,
        lon: lon,
        place: place
    })

    useDelayedInput([search], () => setDelayedSearch(search), 700)

    useEffect(() => {
        // add lat and lon to the selected suggest if not already queried
        if (place && !place.lat && suggests && suggests.find((g) => g.id == place.id) && suggests.find((g) => g.id == place.id).lat) {
            let completePlace = suggests.find((g) => g.id == place.id)
            choosePlace(completePlace)
            setSearch(completePlace.name)
            setDelayedSearch(completePlace.name)
            setOpenLocationSuggest(false)
        }
        if (suggests && lat && lon && !openLocationSuggest) {
            setSearch(suggests[0].name)
            choosePlace(suggests[0])
            setOpenLocationSuggest(false)
        }
    })

    useEffect(() => {
        if (suggests && (!place || !suggests.find(suggest => suggest.id === place.id))) {
            setOpenLocationSuggest(true)
        }
    }, [suggests])

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setLat(null)
        setLon(null)
        setSearch(e.target.value)
        if (e.target.value.length > 2) {
            setOpenLocationSuggest(true)
        }
    }

    const {navigateWithQuery} = useNavigateWithQueryParams()

    const chooseSuggestion = (suggest: Place): void => {
        choosePlace(suggest)
        setSearch(suggest.name)
        setDelayedSearch(suggest.name)
        setOpenLocationSuggest(false)
        if(shouldWriteToUrl){
            navigateWithQuery('');
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            if (suggests && suggests.length >= 1) {
                chooseSuggestion(suggests[0])
            }
            if (!openLocationSuggest && place && place.id && place.lat) {
                onSubmit && onSubmit(place)
            }
        }
    }

    const close = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) {
            return
        }
        setOpenLocationSuggest(false)
    }

    const getGeoLocation = (): void => {
        navigator.geolocation.getCurrentPosition(function (position) {
            setLat(position.coords.latitude)
            setLon(position.coords.longitude)
        });
    }

    const open = (e: MouseEvent<HTMLInputElement>): void => {
        e.currentTarget.select()
        setOpenLocationSuggest(true)
    }

    return (
        <StrictMode>
            <div tabIndex={0} onBlur={close}>
                <div className="relative">
                    <div className={`${className}`}>
                        <div className={`relative max-w-full`}>
                            <input
                                value={search}
                                type="text"
                                autoComplete={'off'}
                                placeholder="PLZ oder Stadt eingeben"
                                className={`
                                    w-full
                                    py-2 px-3 pr-8
                                    rounded ${openLocationSuggest && suggests ? "focus:rounded-b-none focus:border-b-neutral-100" : ""}
                                    border border-neutral-500 focus:border-primary focus:outline-none
                                    `}
                                onChange={onChange}
                                onKeyDown={handleKeyDown}
                                onClick={open}
                            />

                            {loading && (<LoadingIcon
                                className="animate-spin absolute top-1/4 right-9 h-5 w-5"/>)}

                            <div onClick={getGeoLocation}>
                                <LocationIcon/>
                            </div>
                        </div>
                        <div
                            className={`
                                absolute overflow-auto ${openLocationSuggest && suggests ? "" : "hidden"}
                                w-full max-h-[175px] ${className}
                                bg-white z-20
                                rounded rounded-t-none
                                border border-t-0 border-primary
                                text-sm
                                shadow
                                `}>
                            {!loading && suggests && suggests.map((place) => (
                                <div key={place.id} className="px-3 py-2 cursor-pointer font-bold"
                                     onClick={() => chooseSuggestion(place)}>
                                    {place.name}
                                </div>
                            ))}
                        </div>
                        {error && (
                            <div className={'text-warning text-xs mt-2'}>
                                {t('error')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </StrictMode>
    )
}
